import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { FileText, Check, X } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

export type PendingCertItem = {
  id: string
  name: string
  meta?: string | null
  completedDate: string
}

/**
 * Awarding-body PDFs are usually named with the learner's name (e.g.
 * "John_Smith_certificate.pdf"). Suggest — never decide — the most likely match by comparing
 * words in the filename against words in each pending item's name; the admin always confirms
 * or changes the suggestion before anything is saved.
 */
function normaliseWords(s: string): string[] {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ')
    .filter(w => w.length > 1 && !['pdf', 'certificate', 'cert', 'certification'].includes(w))
}

function suggestMatch(fileName: string, candidates: PendingCertItem[]): string | null {
  const fileWords = new Set(normaliseWords(fileName))
  if (fileWords.size === 0) return null

  let best: { id: string; score: number } | null = null
  for (const c of candidates) {
    const nameWords = normaliseWords(c.name)
    const score = nameWords.filter(w => fileWords.has(w)).length
    if (score > 0 && (!best || score > best.score)) best = { id: c.id, score }
  }
  return best?.id ?? null
}

type PendingFile = { file: File; assignedTo: string | null }

/**
 * Pending-list + drag-and-drop batch upload for third-party certificates. Shared by the
 * course-wide Third-party tab, the booking detail page, and the session detail page — each
 * just supplies a scoped `pending` list, an `upload` function for its own record shape
 * (training record vs. session attendee), and a refetch callback.
 */
export default function ThirdPartyCertificateUploader({
  pending, isLoading = false, providerName, emptyMessage, onUploaded, upload,
}: {
  pending: PendingCertItem[]
  isLoading?: boolean
  providerName?: string | null
  emptyMessage?: string
  onUploaded: () => void
  upload: (id: string, file: File) => Promise<unknown>
}) {
  const { formatDate } = useAdminDates()
  const fmtDate = (d: string) => {
    return formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [batch, setBatch] = useState<PendingFile[]>([])
  const [saving, setSaving] = useState(false)
  const [results, setResults] = useState<Record<number, 'ok' | 'error'>>({})

  function addFiles(files: FileList | null) {
    if (!files) return
    const assignedIds = new Set(batch.map(b => b.assignedTo).filter(Boolean))
    const additions: PendingFile[] = Array.from(files).map(file => {
      const available = pending.filter(p => !assignedIds.has(p.id))
      const suggestion = suggestMatch(file.name, available)
      if (suggestion) assignedIds.add(suggestion)
      return { file, assignedTo: suggestion }
    })
    setBatch(b => [...b, ...additions])
    setResults({})
  }

  function updateAssignment(index: number, id: string | null) {
    setBatch(b => b.map((row, i) => i === index ? { ...row, assignedTo: id } : row))
  }

  function removeRow(index: number) {
    setBatch(b => b.filter((_, i) => i !== index))
  }

  async function handleSave() {
    setSaving(true)
    const newResults: Record<number, 'ok' | 'error'> = {}
    for (let i = 0; i < batch.length; i++) {
      const row = batch[i]
      if (!row.assignedTo) continue
      try {
        await upload(row.assignedTo, row.file)
        newResults[i] = 'ok'
      } catch {
        newResults[i] = 'error'
      }
    }
    setResults(newResults)
    setSaving(false)
    onUploaded()
    // Drop successfully-saved rows; keep failures and unassigned rows so nothing is silently lost.
    setBatch(b => b.filter((_, i) => newResults[i] !== 'ok'))
  }

  const assignedElsewhere = (index: number) =>
    new Set(batch.filter((_, i) => i !== index).map(r => r.assignedTo).filter(Boolean))

  const readyCount = batch.filter(r => r.assignedTo).length

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Awaiting a certificate {pending.length > 0 && `(${pending.length})`}
          </span>
        </div>
        {isLoading && <p className="text-sm text-gray-400 px-4 py-6 text-center">Loading…</p>}
        {!isLoading && pending.length === 0 && (
          <p className="text-sm text-gray-400 px-4 py-6 text-center">{emptyMessage ?? 'Nobody is waiting on a certificate.'}</p>
        )}
        {pending.length > 0 && (
          <div className="divide-y divide-gray-100">
            {pending.map(p => (
              <div key={p.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-gray-900">{p.name}</span>
                  {p.meta && <span className="text-gray-400 ml-2">{p.meta}</span>}
                </div>
                <span className="text-xs text-gray-400 shrink-0">Completed {fmtDate(p.completedDate)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {pending.length > 0 && (
        <div>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files) }}
            className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-300 transition-colors"
          >
            <FileText size={22} className="mx-auto text-gray-300 mb-1.5" />
            <p className="text-sm text-gray-600">Click to choose files, or drag and drop</p>
            <p className="text-xs text-gray-400 mt-0.5">PDF certificates from {providerName || 'the provider'}</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={e => { addFiles(e.target.files); e.target.value = '' }}
          />
        </div>
      )}

      {batch.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="divide-y divide-gray-100">
            {batch.map((row, i) => {
              const taken = assignedElsewhere(i)
              const status = results[i]
              return (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                  <FileText size={15} className="text-gray-400 shrink-0" />
                  <span className="text-gray-700 truncate flex-1 min-w-0">{row.file.name}</span>
                  <select
                    value={row.assignedTo ?? ''}
                    onChange={e => updateAssignment(i, e.target.value || null)}
                    disabled={saving}
                    className="text-sm border border-gray-200 rounded-md px-2 py-1.5 max-w-[220px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">— Assign to —</option>
                    {pending.filter(p => !taken.has(p.id)).map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {status === 'ok' && <Check size={15} className="text-green-600 shrink-0" />}
                  {status === 'error' && <span className="text-xs text-red-600 shrink-0">Failed</span>}
                  {!status && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      disabled={saving}
                      className="text-gray-300 hover:text-gray-500 cursor-pointer shrink-0"
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center gap-3">
            <Button size="sm" onClick={handleSave} disabled={saving || readyCount === 0}>
              {saving ? 'Saving…' : `Save ${readyCount} certificate${readyCount === 1 ? '' : 's'}`}
            </Button>
            {batch.length > readyCount && (
              <span className="text-xs text-gray-400">{batch.length - readyCount} not yet assigned</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
